import { getAuthHeader } from "./authHeader";
// frontend/src/services/dealApi.js
import axios from "axios";
// Assuming AuthContext provides a way to get the token,
// otherwise, interceptors or direct token passing is needed.
// For simplicity, this example assumes token is handled by an interceptor or passed manually.
// If using AuthContext: import { useAuth } from '../contexts/AuthContext';

import { DEALS_API } from "../apiConstants";
import { requestErrorMessage } from "../utils/loadError";
const API_URL = DEALS_API;

// Helper function to handle errors consistently
const handleApiError = (error, context = "operation") => {
  const details = error.response?.data?.errors;
  const message = details && typeof details === "object"
    ? Object.entries(details)
        .map(([field, messages]) => `${field.replaceAll("_", " ")}: ${[].concat(messages).join(", ")}`)
        .join("; ")
    : requestErrorMessage(error, `Deal ${context} failed.`);
  error.message = message;
  throw error;
};

// Fields that should be omitted if empty
const omitIfEmpty = ["contact_id", "company_id"];

// Helper: Convert camelCase to snake_case for API payloads
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
    // Skip empty strings for ID fields
    if (
      omitIfEmpty.includes(snakeKey) &&
      (value === "" || value === null || value === undefined)
    ) {
      continue;
    }
    result[snakeKey] = value;
  }
  return result;
};

// Helper: Wrap payload in 'deal' key with snake_case conversion
const wrapDealPayload = (data) => {
  return { deal: toSnakeCase(data) };
};

const dealApi = {
  /**
   * Fetches a list of deals with pagination, filtering, and sorting.
   * @param {object} params - Query parameters (e.g., page, limit, search, stage, sortBy, sortOrder)
   * @returns {Promise<object>} - Promise resolving to { deals: [], page, pages, total }
   */
  getAll: async (params = {}) => {
    try {
      const authConfig = getAuthHeader(); // Get headers before request
      const response = await axios.get(API_URL, { params, ...authConfig });
      // Ensure the response includes pagination data
      return response.data || { deals: [], page: 1, pages: 1, total: 0 };
    } catch (error) {
      handleApiError(error, "fetch");
    }
  },

  /**
   * Fetches a single deal by its ID.
   * @param {string|number} id - The ID of the deal.
   * @returns {Promise<object>} - Promise resolving to the deal object.
   */
  getById: async (id) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(`${API_URL}/${id}`, authConfig);
      return response.data;
    } catch (error) {
      handleApiError(error, `fetch (ID: ${id})`);
    }
  },

  /**
   * Creates a new deal.
   * @param {object} dealData - The data for the new deal.
   * @returns {Promise<object>} - Promise resolving to the newly created deal object.
   */
  create: async (dealData) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.post(
        API_URL,
        wrapDealPayload(dealData),
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "creation");
    }
  },

  /**
   * Updates an existing deal.
   * @param {string|number} id - The ID of the deal to update.
   * @param {object} dealData - The updated data for the deal.
   * @returns {Promise<object>} - Promise resolving to the updated deal object.
   */
  update: async (id, dealData) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.put(
        `${API_URL}/${id}`,
        wrapDealPayload(dealData),
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, `update (ID: ${id})`);
    }
  },

  /**
   * Deletes a deal.
   * @param {string|number} id - The ID of the deal to delete.
   * @returns {Promise<object>} - Promise resolving to an empty object or confirmation message.
   */
  delete: async (id) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.delete(`${API_URL}/${id}`, authConfig);
      return response.data; // Often returns empty body or a success message
    } catch (error) {
      handleApiError(error, `delete (ID: ${id})`);
    }
  },
};

export default dealApi;
